import { useState } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

const consultationTypes = [
  {
    id: 'discovery',
    title: 'Discovery Call',
    duration: '30 minutes',
    price: 'Free',
    description: 'Initial consultation to understand your needs and explore how we can help',
    features: [
      'Project scope assessment',
      'Strategic recommendations',
      'Timeline and budget discussion',
      'Next steps planning'
    ],
    popular: true
  },
  {
    id: 'strategy',
    title: 'Strategy Session',
    duration: '60 minutes',
    price: '$250',
    description: 'Deep-dive strategic consultation with actionable recommendations',
    features: [
      'Comprehensive business analysis',
      'Strategic framework development',
      'Implementation roadmap',
      'Follow-up summary document'
    ]
  },
  {
    id: 'intensive',
    title: 'Intensive Workshop',
    duration: '2 hours',
    price: '$500',
    description: 'Collaborative workshop session to solve specific challenges',
    features: [
      'Problem-solving workshop',
      'Framework development',
      'Team alignment session',
      'Detailed action plan'
    ]
  }
];

const timeSlots = [
  '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
  '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'
];

const availableDates = Array.from({ length: 14 }, (_, i) => {
  const date = new Date();
  date.setDate(date.getDate() + i + 1);
  return date;
});

export default function BookConsultation() {
  const [selectedType, setSelectedType] = useState('discovery');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    projectType: '',
    budget: '',
    message: ''
  });
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSubmit = new FormData();
      formDataToSubmit.append('consultation_type', selectedType);
      formDataToSubmit.append('date', selectedDate);
      formDataToSubmit.append('time', selectedTime);
      formDataToSubmit.append('name', formData.name);
      formDataToSubmit.append('email', formData.email);
      formDataToSubmit.append('company', formData.company || '');
      formDataToSubmit.append('phone', formData.phone || '');
      formDataToSubmit.append('project_type', formData.projectType);
      formDataToSubmit.append('budget', formData.budget || '');
      formDataToSubmit.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5r551k98t1oshnre4eg', {
        method: 'POST',
        body: formDataToSubmit
      });

      if (response.ok) {
        setSubmitStatus('success');
        setStep(4);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getShortDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const selectedConsultationType = consultationTypes.find(type => type.id === selectedType);

  return (
    <div className="min-h-screen bg-stone-50">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="font-['Playfair_Display'] text-5xl md:text-7xl font-bold text-stone-900 mb-6">
              Book Your Consultation
            </h1>
            <p className="text-xl md:text-2xl text-stone-600 font-light leading-relaxed mb-12">
              Schedule a strategic consultation to explore how we can accelerate your business transformation
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 pb-20">
        {step < 4 && (
          <div className="flex justify-center mb-12">
            <div className="flex items-center gap-4">
              {[1, 2, 3].map((stepNumber) => (
                <div key={stepNumber} className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                    step >= stepNumber 
                      ? 'bg-yellow-600 text-white' 
                      : 'bg-stone-200 text-stone-500'
                  }`}>
                    {stepNumber}
                  </div>
                  {stepNumber < 3 && (
                    <div className={`w-20 h-0.5 ${
                      step > stepNumber ? 'bg-yellow-600' : 'bg-stone-200'
                    }`}></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-stone-900 mb-4">
                Choose Your Consultation Type
              </h2>
              <p className="text-stone-600">
                Select the consultation format that best fits your needs
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {consultationTypes.map((type) => (
                <div
                  key={type.id}
                  className={`relative p-8 rounded-2xl border-2 cursor-pointer transition-all transform hover:scale-105 ${
                    selectedType === type.id
                      ? 'border-yellow-600 bg-yellow-50 shadow-lg'
                      : 'border-stone-200 bg-white hover:border-stone-300'
                  }`}
                  onClick={() => setSelectedType(type.id)}
                >
                  {type.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-yellow-600 text-white px-4 py-1 rounded-full text-xs font-bold">
                        MOST POPULAR
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center mb-6">
                    <h3 className="font-['Playfair_Display'] text-2xl font-bold text-stone-900 mb-2">
                      {type.title}
                    </h3>
                    <div className="flex items-center justify-center gap-2 text-stone-600 mb-2">
                      <i className="ri-time-line"></i>
                      <span>{type.duration}</span>
                    </div>
                    <div className="text-3xl font-bold text-yellow-600">
                      {type.price}
                    </div>
                  </div>

                  <p className="text-stone-600 text-center mb-6">
                    {type.description}
                  </p>

                  <ul className="space-y-3">
                    {type.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <i className="ri-check-line text-yellow-600 mt-1"></i>
                        <span className="text-stone-600 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {selectedType === type.id && (
                    <div className="absolute inset-0 rounded-2xl border-2 border-yellow-600 pointer-events-none">
                      <div className="absolute top-4 right-4 w-6 h-6 bg-yellow-600 rounded-full flex items-center justify-center">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="text-center">
              <button
                onClick={() => setStep(2)}
                className="bg-yellow-600 hover:bg-yellow-700 text-white px-12 py-4 rounded-full font-medium transition-all transform hover:scale-105 whitespace-nowrap"
              >
                Continue to Date & Time
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-stone-900 mb-4">
                Select Date & Time
              </h2>
              <p className="text-stone-600 mb-2">
                {selectedConsultationType?.title} - {selectedConsultationType?.duration}
              </p>
              <p className="text-sm text-stone-500">
                All times shown in Eastern Time (EST)
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12">
              {/* Date Selection */}
              <div>
                <h3 className="font-['Playfair_Display'] text-xl font-bold text-stone-900 mb-6">
                  Choose a Date
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {availableDates.map((date) => {
                    const dateStr = date.toISOString().split('T')[0];
                    const isSelected = selectedDate === dateStr;
                    
                    return (
                      <button
                        key={dateStr}
                        onClick={() => setSelectedDate(dateStr)}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                          isSelected
                            ? 'border-yellow-600 bg-yellow-50 shadow-md'
                            : 'border-stone-200 bg-white hover:border-stone-300'
                        }`}
                      >
                        <div className={`font-bold ${isSelected ? 'text-yellow-600' : 'text-stone-900'}`}>
                          {getShortDate(date)}
                        </div>
                        <div className="text-sm text-stone-600">
                          {date.toLocaleDateString('en-US', { weekday: 'long' })}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Time Selection */}
              <div>
                <h3 className="font-['Playfair_Display'] text-xl font-bold text-stone-900 mb-6">
                  Available Times
                </h3>
                {selectedDate ? (
                  <div className="grid grid-cols-2 gap-3">
                    {timeSlots.map((time) => {
                      const isSelected = selectedTime === time;
                      
                      return (
                        <button
                          key={time}
                          onClick={() => setSelectedTime(time)}
                          className={`p-3 rounded-lg border-2 text-center transition-all ${
                            isSelected
                              ? 'border-yellow-600 bg-yellow-50 shadow-md text-yellow-600'
                              : 'border-stone-200 bg-white hover:border-stone-300 text-stone-900'
                          }`}
                        >
                          {time}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 text-stone-500">
                    <i className="ri-calendar-line text-4xl mb-4 block"></i>
                    <p>Please select a date first</p>
                  </div>
                )}
              </div>
            </div>

            {selectedDate && selectedTime && (
              <div className="mt-12 p-6 bg-yellow-50 border border-yellow-200 rounded-2xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-bold text-stone-900 mb-2">Selected Appointment</h4>
                    <p className="text-stone-600">
                      {selectedConsultationType?.title} on {formatDate(new Date(selectedDate))} at {selectedTime} EST
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-yellow-600">
                      {selectedConsultationType?.price}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between mt-12">
              <button
                onClick={() => setStep(1)}
                className="border-2 border-stone-300 text-stone-700 hover:border-stone-400 hover:text-stone-900 px-8 py-4 rounded-full font-medium transition-all whitespace-nowrap"
              >
                Back to Consultation Type
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={!selectedDate || !selectedTime}
                className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-stone-400 text-white px-12 py-4 rounded-full font-medium transition-all transform hover:scale-105 disabled:transform-none whitespace-nowrap"
              >
                Continue to Details
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-stone-900 mb-4">
                Your Information
              </h2>
              <p className="text-stone-600">
                Tell us about yourself and your project needs
              </p>
            </div>

            <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-stone-900 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent transition-all text-sm"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-stone-900 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent transition-all text-sm"
                    placeholder="your.email@company.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-stone-900 mb-2">
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent transition-all text-sm"
                    placeholder="Your company"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-stone-900 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent transition-all text-sm"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="projectType" className="block text-sm font-medium text-stone-900 mb-2">
                  Project Type *
                </label>
                <select
                  id="projectType"
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent transition-all text-sm"
                >
                  <option value="">Select project type</option>
                  <option value="digital-strategy">Digital Strategy & Consulting</option>
                  <option value="pmo-implementation">PMO Implementation</option>
                  <option value="website-development">Website Development</option>
                  <option value="process-optimization">Process Optimization & ISO</option>
                  <option value="digital-marketing">Digital Marketing & SEO</option>
                  <option value="pm-mentorship">PM Launchpad Mentorship</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="budget" className="block text-sm font-medium text-stone-900 mb-2">
                  Project Budget Range
                </label>
                <select
                  id="budget"
                  name="budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent transition-all text-sm"
                >
                  <option value="">Select budget range</option>
                  <option value="10k-25k">$10K - $25K</option>
                  <option value="25k-50k">$25K - $50K</option>
                  <option value="50k-100k">$50K - $100K</option>
                  <option value="100k-250k">$100K - $250K</option>
                  <option value="250k+">$250K+</option>
                  <option value="not-sure">Not sure yet</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-stone-900 mb-2">
                  Tell us about your project *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent transition-all text-sm resize-none"
                  placeholder="What are your main goals and challenges? What does success look like to you?"
                />
                <p className="text-xs text-stone-500 mt-2">
                  {formData.message.length}/500 characters
                </p>
              </div>

              <div className="bg-stone-50 p-6 rounded-2xl">
                <h4 className="font-bold text-stone-900 mb-4">Booking Summary</h4>
                <div className="space-y-2 text-sm text-stone-600">
                  <div className="flex justify-between">
                    <span>Consultation Type:</span>
                    <span className="font-medium text-stone-900">{selectedConsultationType?.title}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Date & Time:</span>
                    <span className="font-medium text-stone-900">{formatDate(new Date(selectedDate))} at {selectedTime} EST</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Duration:</span>
                    <span className="font-medium text-stone-900">{selectedConsultationType?.duration}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2 mt-4">
                    <span className="font-bold text-stone-900">Total:</span>
                    <span className="font-bold text-xl text-yellow-600">{selectedConsultationType?.price}</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="border-2 border-stone-300 text-stone-700 hover:border-stone-400 hover:text-stone-900 px-8 py-4 rounded-full font-medium transition-all whitespace-nowrap"
                >
                  Back to Date & Time
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-stone-400 text-white px-12 py-4 rounded-full font-medium transition-all transform hover:scale-105 disabled:transform-none whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-3">
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                      Booking Consultation...
                    </div>
                  ) : (
                    'Book Consultation'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {step === 4 && (
          <div className="max-w-2xl mx-auto text-center">
            {submitStatus === 'success' ? (
              <div>
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
                  <i className="ri-check-line text-4xl text-green-600"></i>
                </div>
                <h2 className="font-['Playfair_Display'] text-4xl font-bold text-stone-900 mb-6">
                  Consultation Booked Successfully!
                </h2>
                <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-8 mb-8">
                  <h3 className="font-bold text-stone-900 mb-4">Your Appointment Details</h3>
                  <div className="space-y-2 text-stone-700">
                    <p><strong>Type:</strong> {selectedConsultationType?.title}</p>
                    <p><strong>Date:</strong> {formatDate(new Date(selectedDate))}</p>
                    <p><strong>Time:</strong> {selectedTime} EST</p>
                    <p><strong>Duration:</strong> {selectedConsultationType?.duration}</p>
                  </div>
                </div>
                <p className="text-lg text-stone-600 mb-8">
                  You'll receive a confirmation email with calendar invite and meeting details within the next few minutes.
                </p>
                <div className="space-y-4">
                  <p className="text-stone-600">
                    <strong>What's Next?</strong> We'll send you a brief questionnaire to help us prepare for our session and make the most of our time together.
                  </p>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/')}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white px-8 py-4 rounded-full font-medium transition-all transform hover:scale-105 whitespace-nowrap"
                  >
                    Return to Homepage
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-8">
                  <i className="ri-error-warning-line text-4xl text-red-600"></i>
                </div>
                <h2 className="font-['Playfair_Display'] text-4xl font-bold text-stone-900 mb-6">
                  Something Went Wrong
                </h2>
                <p className="text-lg text-stone-600 mb-8">
                  We encountered an issue processing your booking. Please try again or contact us directly.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => setStep(3)}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white px-8 py-4 rounded-full font-medium transition-all transform hover:scale-105 whitespace-nowrap"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/contact')}
                    className="border-2 border-stone-300 text-stone-700 hover:border-stone-400 hover:text-stone-900 px-8 py-4 rounded-full font-medium transition-all whitespace-nowrap"
                  >
                    Contact Us
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}